import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Col, Row } from 'reactstrap';
import PropTypes from 'prop-types';
import { FieldArray, Form } from 'formik';
import { isEmpty } from 'lodash';
import { useDispatch } from 'react-redux';
import { constantToSelect } from '../../common/helpers/constantToSelect';
import FormSelectField from '../../common/components/formFields/FormSelectField';
import CommonButton from '../../common/components/CommonButton';
import { CommonButtonVariants } from '../../constants/CommonButtonVariants';
import FormDatePickerField from '../../common/components/formFields/FormDatePickerField';
import calendarIcon from '../../assets/img/icons/calendar_month.svg';
import FormInputField, {
  FormInputFieldType,
} from '../../common/components/formFields/FormInputField';
import {
  ServiceHistory,
  ServiceHistoryName,
  ServiceTypeName,
} from '../../constants/ServiceHistory';
import AddVehicleContentWrapper from '../AddVehicleContentWrapper';
import SettingsSectionHeader from '../../settings/SettingsSectionHeader';
import plusIcon from '../../assets/img/plusIcon.svg';
import CommonDeleteIconButton from '../../common/components/CommonDeleteIconButton';
import AddServiceHistoryPhoto from './AddServiceHistoryPhoto';
import DeleteServiceHistoryModal from './DeleteServiceHistoryModal';
import { clearPhotosInServiceHistory } from '../AddVehicleActions';
import '../../assets/css/componentStyles/FormValidation.css';

function ServiceHistoryForm({
  form,
  title = 'Service History',
  description = "Add details of the vehicle's service history.",
  className = '',
  labelVariant = 'bordered',
  validationErrors = {},
}) {
  const dispatch = useDispatch();

  const [uiState, setUiState] = useState({
    isDeleteModalOpen: false,
    selectedServiceHistory: null,
    deleteArrayHelpers: null,
    deleteIndex: null,
  });

  const [isNoServiceHistoryConfirmed, setIsNoServiceHistoryConfirmed] = useState(false);

  // --- CORRECTED ERROR HANDLING ---
  const serverError = validationErrors?.SERVICE_HISTORY?.serviceHistory;
  const clientErrors = validationErrors?.SERVICE_HISTORY?.serviceHistoryForm;
  const clientFormError = typeof clientErrors === 'string' ? clientErrors : null;
  const clientFieldErrors =
    typeof clientErrors === 'object' && clientErrors !== null ? clientErrors : {};
  // --- END ERROR HANDLING ---
  const hasSelectedType = !!form.values.serviceHistory?.type;

  const serviceHistoryList = useMemo(() => constantToSelect(ServiceHistoryName), []);
  const serviceTypeList = useMemo(() => constantToSelect(ServiceTypeName), []);

  const serviceHistoryValues = useMemo(
    () => form.values.serviceHistory || {},
    [form.values.serviceHistory],
  );

  const manualRecords = useMemo(
    () => serviceHistoryValues.manualRecords || [],
    [serviceHistoryValues.manualRecords],
  );
  const currentServiceType = useMemo(
    () => serviceHistoryValues?.type?.value,
    [serviceHistoryValues?.type?.value],
  );

  const isAddButtonDisabled = useMemo(() => {
    if (!manualRecords.length) return true;
    return manualRecords.some(
      (record) =>
        isEmpty(record?.date) ||
        isEmpty(record?.serviceType) ||
        isEmpty(record?.mileage?.toString()) ||
        isEmpty(record?.serviceName),
    );
  }, [manualRecords]);

  const shouldShowForm = useMemo(
    () =>
      currentServiceType === ServiceHistory.FULL ||
      currentServiceType === ServiceHistory.PARTIAL,
    [currentServiceType],
  );

  const isNoServiceHistory = useMemo(
    () => currentServiceType === ServiceHistory.NONE && !isNoServiceHistoryConfirmed,
    [currentServiceType, isNoServiceHistoryConfirmed],
  );
  const content = useMemo(
    () => ({
      title,
      description,
      noHistoryAlert: {
        question: 'Are you sure this vehicle has No Service History?',
        noLabel: 'NO',
        yesLabel: 'YES',
      },
      addServiceButton: 'Add another service',
      photoInstruction: 'Please upload scans or photos of the service book',
    }),
    [title, description],
  );

  const handleDeleteModalOpen = useCallback((serviceHistory, arrayHelpers, index) => {
    setUiState({
      isDeleteModalOpen: true,
      selectedServiceHistory: serviceHistory,
      deleteArrayHelpers: arrayHelpers,
      deleteIndex: index,
    });
  }, []);

  const handleDeleteModalClose = useCallback(() => {
    setUiState({
      isDeleteModalOpen: false,
      selectedServiceHistory: null,
      deleteArrayHelpers: null,
      deleteIndex: null,
    });
  }, []);

  const handleServiceDelete = useCallback(() => {
    if (uiState.deleteArrayHelpers && uiState.deleteIndex !== null) {
      uiState.deleteArrayHelpers.remove(uiState.deleteIndex);
      handleDeleteModalClose();
    }
  }, [uiState.deleteArrayHelpers, uiState.deleteIndex, handleDeleteModalClose]);

  const handleAddAnotherService = useCallback((arrayHelpers) => {
    arrayHelpers.push({
      date: '',
      serviceType: '',
      mileage: '',
      serviceName: '',
    });
  }, []);

  useEffect(() => {
    if (currentServiceType !== ServiceHistory.NONE) {
      setIsNoServiceHistoryConfirmed(false);
    }
  }, [currentServiceType]);

  useEffect(() => {
    if (
      (currentServiceType === ServiceHistory.FULL ||
        currentServiceType === ServiceHistory.PARTIAL) &&
      manualRecords.length === 0
    ) {
      form.setFieldValue('serviceHistory.manualRecords', [
        {
          date: '',
          serviceType: '',
          mileage: '',
          serviceName: '',
        },
      ]);
    }
  }, [currentServiceType, manualRecords.length, form]);

  return (
    <AddVehicleContentWrapper className={className}>
      <SettingsSectionHeader header={content.title} description={content.description} />

      {serverError && !form.touched?.serviceHistory?.type && (
        <div className="field-error-message">{serverError}</div>
      )}
      {clientFormError &&
        !serverError &&
        !hasSelectedType &&
        !form.touched?.serviceHistory?.type && (
          <div className="field-error-message">{clientFormError}</div>
        )}

      <Row className="mt-4">
        <Col>
          <Form autoComplete="off">
            <Row>
              <Col className="ml-auto mr-auto " md={12}>
                <div className="service-history__container">
                  <Row>
                    <Col sm={12} md={6}>
                      <FormSelectField
                        name="serviceHistory.type"
                        label="Service History Type"
                        options={serviceHistoryList}
                        required
                        labelVariant={labelVariant}
                        form={form}
                        blurInputOnSelect={false}
                      />
                    </Col>
                  </Row>
                  {isNoServiceHistory && (
                    <Row className="mt-2 mb-4">
                      <Col md={12}>
                        <div className="service-history__no-history-alert">
                          <div className="service-history__no-history-content">
                            <div className="service-history__no-history-icon">
                              <i className="fa fa-exclamation" />
                            </div>
                            <div className="service-history__no-history-text">
                              {content.noHistoryAlert.question}
                            </div>
                          </div>
                        </div>
                        <Row className="mt-3">
                          <Col className="d-flex justify-content-center gap-3" md={3}>
                            <CommonButton
                              label={content.noHistoryAlert.yesLabel}
                              handleClick={() => {
                                const noneOption = serviceHistoryList.find(
                                  (option) => option.value === ServiceHistory.NONE,
                                );

                                form.setFieldValue('serviceHistory.type', noneOption);
                                setIsNoServiceHistoryConfirmed(true);

                                form.setFieldValue('serviceHistory.manualRecords', []);
                                form.setFieldValue('serviceHistory.photoRecords', []);

                                // Clear photoRecords from Redux state
                                dispatch(clearPhotosInServiceHistory());

                                form.setFieldTouched('serviceHistory.type', true);
                              }}
                              variant={CommonButtonVariants.PRIMARY}
                              className="w-100"
                              type="button"
                            />
                          </Col>
                          <Col className="d-flex justify-content-center gap-3" md={3}>
                            <CommonButton
                              label={content.noHistoryAlert.noLabel}
                              handleClick={() => {
                                form.setFieldValue('serviceHistory.type', null);
                                setIsNoServiceHistoryConfirmed(false);
                              }}
                              variant={CommonButtonVariants.OUTLINED_RED}
                              className="w-100"
                              type="button"
                            />
                          </Col>
                        </Row>
                      </Col>
                    </Row>
                  )}
                  {shouldShowForm && (
                    <>
                      <p className="service-history__form-title mt-3 mb-2">
                        Fill the form below
                      </p>
                      <FieldArray name="serviceHistory.manualRecords">
                        {(arrayHelpers) => (
                          <>
                            {manualRecords?.map((history, index) => (
                              // eslint-disable-next-line react/no-array-index-key
                              <Row className="mt-4 position-relative" key={index}>
                                <Col md={3}>
                                  <FormDatePickerField
                                    label="Date of Service"
                                    name={`serviceHistory.manualRecords.${index}.date`}
                                    icon={calendarIcon}
                                    isPastDateDisabled={false}
                                    required
                                    labelVariant={labelVariant}
                                    form={form}
                                    iconClassName="calendar-icon"
                                  />
                                  {/* SIMPLIFIED FIELD ERROR DISPLAY */}
                                  {clientFieldErrors.Date && (
                                    <div className="field-error-message">
                                      {clientFieldErrors.Date}
                                    </div>
                                  )}
                                </Col>
                                <Col md={3}>
                                  <FormSelectField
                                    name={`serviceHistory.manualRecords.${index}.serviceType`}
                                    label="Service Type"
                                    options={serviceTypeList}
                                    required
                                    labelVariant={labelVariant}
                                    form={form}
                                  />
                                </Col>
                                <Col md={3}>
                                  <FormInputField
                                    name={`serviceHistory.manualRecords.${index}.mileage`}
                                    label="Mileage"
                                    type={FormInputFieldType.NUMBER}
                                    required
                                    labelVariant={labelVariant}
                                    form={form}
                                    min="1"
                                  />
                                  {form.values?.serviceHistory?.manualRecords[index]
                                    ?.mileage &&
                                    Number(
                                      form.values.serviceHistory.manualRecords[index].mileage,
                                    ) < 1 && (
                                      <div className="field-error-message">
                                        Mileage must be 1 or greater.
                                      </div>
                                    )}
                                  {/* SIMPLIFIED FIELD ERROR DISPLAY */}
                                  {clientFieldErrors.Mileage && (
                                    <div className="field-error-message">
                                      {clientFieldErrors.Mileage}
                                    </div>
                                  )}
                                </Col>
                                <Col md={3}>
                                  <div className="d-flex w-100">
                                    <div className="d-flex flex-column w-100">
                                      <FormInputField
                                        name={`serviceHistory.manualRecords.${index}.serviceName`}
                                        label="Service name"
                                        required
                                        labelVariant={labelVariant}
                                        form={form}
                                      />
                                      {/* SIMPLIFIED FIELD ERROR DISPLAY */}
                                      {clientFieldErrors['Service name'] && (
                                        <div className="field-error-message">
                                          {clientFieldErrors['Service name']}
                                        </div>
                                      )}
                                    </div>
                                    {manualRecords.length > 1 && (
                                      <button
                                        type="button"
                                        className="service-history__delete-history-wrapper"
                                        onClick={() => {
                                          handleDeleteModalOpen(history, arrayHelpers, index);
                                        }}
                                      >
                                        <CommonDeleteIconButton />
                                      </button>
                                    )}
                                  </div>
                                </Col>
                              </Row>
                            ))}
                            <Row className="mt-4">
                              <Col md={6}>
                                <CommonButton
                                  label={content.addServiceButton}
                                  disabled={isAddButtonDisabled}
                                  variant={CommonButtonVariants.OUTLINED}
                                  handleClick={() => handleAddAnotherService(arrayHelpers)}
                                  className="w-100"
                                  icon={plusIcon}
                                  iconLeft
                                  iconAlt="plus"
                                />
                              </Col>
                            </Row>
                          </>
                        )}
                      </FieldArray>
                      <Row className="mt-4">
                        <Col md={12}>
                          <p className="font-weight-bold">{content.photoInstruction}</p>
                        </Col>
                        <AddServiceHistoryPhoto form={form} />
                      </Row>
                    </>
                  )}
                </div>
              </Col>
            </Row>
          </Form>
        </Col>
      </Row>
      <DeleteServiceHistoryModal
        isOpen={uiState.isDeleteModalOpen}
        toggle={handleDeleteModalClose}
        onConfirm={handleServiceDelete}
        serviceHistoryData={uiState.selectedServiceHistory}
        isDeleting={false}
      />
    </AddVehicleContentWrapper>
  );
}

ServiceHistoryForm.propTypes = {
  form: PropTypes.object.isRequired,
  title: PropTypes.string,
  description: PropTypes.string,
  className: PropTypes.string,
  labelVariant: PropTypes.oneOf(['bordered', 'standard', 'minimal']),
  validationErrors: PropTypes.object,
};

ServiceHistoryForm.defaultProps = {
  title: 'Service History',
  description: "Add details of the vehicle's service history.",
  className: '',
  labelVariant: 'bordered',
  validationErrors: {},
};

export default ServiceHistoryForm;
